import { useCallback } from 'react';
import { toast } from 'sonner';

type ActionType = 'create' | 'update' | 'delete';
type EntityType = 'expense' | 'lien' | 'allocation';

const SUCCESS_MESSAGES: Record<ActionType, Record<EntityType, string>> = {
  create: {
    expense: 'Expense added',
    lien: 'Lien added',
    allocation: 'Allocation added',
  },
  update: {
    expense: 'Changes saved',
    lien: 'Changes saved',
    allocation: 'Allocations updated',
  },
  delete: {
    expense: 'Expense deleted',
    lien: 'Lien deleted',
    allocation: 'Allocation deleted',
  },
};

const DEFAULT_ERROR_MESSAGE = 'Failed to save. Please try again.';

/**
 * Hook for consistent ledger-related toast notifications.
 * Provides predefined messages for common ledger operations.
 */
export const useLedgerToast = () => {
  const showSuccess = useCallback((action: ActionType, entity: EntityType) => {
    const message = SUCCESS_MESSAGES[action][entity];
    toast.success(message);
  }, []);

  const showError = useCallback((message?: string) => {
    toast.error(message ?? DEFAULT_ERROR_MESSAGE);
  }, []);

  const pipExhausted = useCallback(() => {
    toast.warning('PIP coverage exhausted', {
      description: 'Coverage utilization has reached 100%',
    });
  }, []);

  const medpayExhausted = useCallback(() => {
    toast.warning('MedPay coverage exhausted', {
      description: 'Coverage utilization has reached 100%',
    });
  }, []);

  return {
    showSuccess,
    showError,
    pipExhausted,
    medpayExhausted,
  };
};
